import React, { useMemo } from 'react';
import { useQuery } from '@common-fe/common-fe';
import { toString } from 'lodash';

import { api } from '@/api';
import { API_URL, IContributionSegmentResponseDto } from '@/dtos';

import {
  ContributionSegment,
  ContributionSegmentType,
  ManualContributionFormFields,
  ManualContributionFormValues,
  NewContributionType,
  TaxType,
} from '../contributions.types';
import { useAllValuesForManualContributionsStore } from '../store';


const formatData = (data?: IContributionSegmentResponseDto) => {
  if (!data) return undefined;

  const newItem: ContributionSegment = {
    id: toString(data?.id),
    contributionRequestId: toString(data?.contribution_request_id),
    contributionId: data?.contribution_id,
    contributionDate: data?.contribution_date as unknown as string,
    taxType: data?.tax_type as unknown as TaxType,
    taxYear: data?.tax_year,
    contributionSegmentType: data?.contribution_segment_type as unknown as ContributionSegmentType,
    memo: data?.memo,
    segmentSubsidiaryIds: data?.segment_subsidiary_ids?.map((id) => toString(id)),
    segmentPayrollGroupCodes: data?.segment_payroll_group_codes,
    segmentPlanCodes: data?.segment_plan_codes,
    segmentEmployeeIds: data?.segment_employee_ids?.map((id) => toString(id)),
  };
  return newItem;
};

const QUERY_KEY = 'GET_CONTRIBUTION_SEGMENT_BY_ID';

interface Props {
  contributionId?: string;
  setFormValues?: React.Dispatch<React.SetStateAction<ManualContributionFormValues>>;
}

const useGetContributionSegmentByIdQuery = (props: Props) => {
  const store = useAllValuesForManualContributionsStore();
  const { data, isLoading, isSuccess } = useQuery(
    [QUERY_KEY],
    () =>
      api.get(API_URL.SEGMENTS, {
        params: {
          contribution_id: props?.contributionId,
        },
      }),
    {
      cacheTime: 500,
      enabled: Boolean(props?.contributionId),
      onSuccess: (response) => {
        if (!response?.data) return;

        const segment = formatData(response.data);

        if (segment?.segmentPlanCodes?.length) {
          store.handleSetPlanCodes(segment.segmentPlanCodes);
        }

        if (segment?.segmentSubsidiaryIds?.length) {
          store.handleSetSubsidiaries(segment.segmentSubsidiaryIds);
        }

        if (segment?.segmentPayrollGroupCodes?.length) {
          store.handleSetPayrolls(segment.segmentPayrollGroupCodes);
        }

        if (segment?.segmentEmployeeIds?.length) {
          store.handleSetEmployees(segment.segmentEmployeeIds);
        }

        if (props?.setFormValues) {
          props.setFormValues({
            ...(segment?.contributionSegmentType === ContributionSegmentType.SINGLE
              ? {
                  [ManualContributionFormFields.CONTRIBUTION_TYPE]: ContributionSegmentType.SINGLE,
                  [ManualContributionFormFields.EMPLOYEE]: segment?.segmentEmployeeIds?.shift(),
                }
              : {
                  [ManualContributionFormFields.CONTRIBUTION_TYPE]: NewContributionType.GROUP,
                  [ManualContributionFormFields.GROUP_TYPE]: segment?.contributionSegmentType,
                  [ManualContributionFormFields.PLANS]: segment?.segmentPlanCodes,
                  [ManualContributionFormFields.SUBSIDIARY]: segment?.segmentSubsidiaryIds,
                  [ManualContributionFormFields.PAYROLL_GROUP]: segment?.segmentPayrollGroupCodes,
                }),
            // [ManualContributionFormFields.CONTRIBUTION_DATE]: dayjs(segment?.contributionDate).format(DEFAULT_DATE_FORMAT),
            [ManualContributionFormFields.TAX_YEAR]: segment?.taxYear,
            [ManualContributionFormFields.TAX_TYPE]: segment?.taxType,
            [ManualContributionFormFields.MEMO]: segment?.memo,
          } as ManualContributionFormValues);
        }
      },
    }
  );

  const formattedData = useMemo<ContributionSegment | undefined>(() => formatData(data?.data), [data]);

  return {
    isLoading,
    formattedData,
    isSuccess,
  };
};

export default useGetContributionSegmentByIdQuery;
